import VERTC from '@volcengine/rtc';
import openAPIs from './api';

const APP_ID = '67adf9d0d35c9e01792239ae';
const BUSINESS_ID = '';
const BotName = 'RobotMan_'; // 自定义智能体名称
const CommandKey = 'ctrl';

/**
 * 指令类型（JS 中用对象模拟枚举）
 */
const COMMAND = {
  EXTERNAL_TEXT_TO_SPEECH: 'ExternalTextToSpeech' // 发送文本转语音指令
};

/**
 * 打断优先级（JS 中用对象模拟枚举）
 */
const INTERRUPT_PRIORITY = {
  NONE: 0,       // 占位
  HIGH: 1,       // 高优先级（终止当前交互）
  MEDIUM: 2,     // 中优先级（当前交互结束后播放）
  LOW: 3         // 低优先级（空闲时播放，忙碌时丢弃）
};

/**
 * RTC Client
 */
export class RtcClient {
  constructor() {
    this.engine = null; // RTC 引擎实例
    this.config = null; // 配置参数
    this.audioBotEnabled = false; // AI Bot 是否启用
  }

  /**
   * 将字符串包装成 TLV 格式
   */
  stringToTLV(inputString, type = '') {
    const typeBuffer = new Uint8Array(4);
    // 填充类型缓冲区
    for (let i = 0; i < type.length; i++) {
      typeBuffer[i] = type.charCodeAt(i);
    }
    // 编码文本内容
    const valueBuffer = new TextEncoder().encode(inputString);
    const lengthBuffer = new Uint32Array(1);
    lengthBuffer[0] = valueBuffer.length; // 内容长度

    // 构建 TLV 缓冲区（类型4字节 + 长度4字节 + 内容n字节）
    const tlvBuffer = new Uint8Array(typeBuffer.length + 4 + valueBuffer.length);
    tlvBuffer.set(typeBuffer, 0); // 写入类型

    // 写入长度（大端字节序）
    tlvBuffer[4] = (lengthBuffer[0] >> 24) & 0xff;
    tlvBuffer[5] = (lengthBuffer[0] >> 16) & 0xff;
    tlvBuffer[6] = (lengthBuffer[0] >> 8) & 0xff;
    tlvBuffer[7] = lengthBuffer[0] & 0xff;

    tlvBuffer.set(valueBuffer, 8); // 写入内容
    return tlvBuffer.buffer;
  }

  /**
   * 初始化 RTC 引擎
   */
  createEngine(props) {
    this.config = props;
    this.engine = VERTC.createEngine(this.config.appId);
  }

  /**
   * 监听 RTC 事件
   */
  addEventListeners(events) {
    for (const event of Object.keys(events)) {
      this.engine.on(event, events[event]);
    }
  }

  /**
   * 移除 RTC 事件监听器
   */
  removeEventListeners(events) {
    if (!this.engine) {
      return;
    }
    for (const event of Object.keys(events)) {
      this.engine.off(event, events[event]);
    }
  }

  /**
   * 获取音频设备列表
   */
  async getDevices() {
    const audioInputs = await VERTC.enumerateAudioCaptureDevices();
    return {
      audioInputs: audioInputs.map(input => input.deviceId)
    };
  }

  /**
   * 开启音频采集
   */
  startAudioCapture = async (mic) => {
    if (!this.engine) {
      console.log('开启音频采集失败：RTC 引擎未初始化');
      throw new Error('RTC 引擎未初始化');
    }
    await this.engine.startAudioCapture(mic);
  };

  /**
   * 停止音频采集
   */
  stopAudioCapture = async () => {
    if (!this.engine) {
      console.log('停止音频采集失败：RTC 引擎未初始化');
      return;
    }
    try {
      await this.engine.stopAudioCapture();
    } catch (error) {
      console.log('停止音频采集时出错（可能音频采集未启动或已清理）:', error.message || error);
    }
  };

  /**
   * 推流
   */
  publishStream(mediaType) {
    this.engine.publishStream(mediaType);
  }

  /**
   * 停止推流
   */
  unpublishStream(mediaType) {
    this.engine.unpublishStream(mediaType);
  }

  /**
   * 加入房间
   */
  joinRoom({ token, username }) {
    return this.engine.joinRoom(
      token,
      `${this.config.roomId}`,
      {
        userId: this.config.userId,
        extraInfo: JSON.stringify({
          user_name: username || this.config.userId,
          user_id: this.config.userId
        })
      },
      {
        isAutoPublish: true, // 进房后自动推流
        isAutoSubscribeAudio: true // 自动订阅音频
      }
    );
  }

  /**
   * 离开房间
   */
  leaveRoom() {
    this.stopAudioBot(this.config.roomId, this.config.userId);
    this.engine.leaveRoom();
  }

  /**
   * 启用 AI Bot
   */
  async startAudioBot(roomId, userId, config) {
    if (this.audioBotEnabled) {
      await this.stopAudioBot(roomId, userId);
    }
    try {
      // 调用 OpenAPI 启用 AI Bot（需根据实际接口参数调整）
      const apiResult = await openAPIs.StartVoiceChat(config);
      // 设置状态
      this.audioBotEnabled = true;
      return apiResult;
    } catch (error) {
      // 确保失败时状态为 false
      this.audioBotEnabled = false;
      throw error;
    }
  }

  /**
   * 停止 AI Bot
   */
  async stopAudioBot(roomId, userId) {
    if (this.audioBotEnabled) {
      await openAPIs.StopVoiceChat({
        AppId: APP_ID,
        BusinessId: BUSINESS_ID,
        RoomId: roomId,
        TaskId: userId
      });
      this.audioBotEnabled = false;
    }
  }

  /**
   * 打断智能体说话
   */
  async stopAudioVoice(roomId, userId) {
    if (this.audioBotEnabled) {
      const res = await openAPIs.UpdateVoiceChat({
        AppId: APP_ID,
        BusinessId: BUSINESS_ID,
        RoomId: roomId,
        TaskId: userId,
        Command: 'interrupt'
      });
      return res;
    }
    return Promise.reject(new Error('AI 打断失败：Bot 未启用'));
  }

  /**
   * 插入阶段性 Prompt 到 LLM
   * @param {string} roomId - 房间 ID
   * @param {string} userId - 用户 ID
   * @param {string} promptMessage - 要插入的 prompt 内容
   * @returns {Promise} - API 调用结果
   */
  async insertStagePrompt(roomId, userId, promptMessage) {
    if (!this.audioBotEnabled) {
      console.warn('[阶段性 Prompt] 插入失败：AI Bot 未启用');
      return Promise.reject(new Error('AI Bot 未启用'));
    }

    try {

      const res = await openAPIs.UpdateVoiceChat({
        AppId: APP_ID,
        BusinessId: BUSINESS_ID,
        RoomId: roomId,
        TaskId: userId,
        Command: 'ExternalPromptsForLLM',
        Message: promptMessage
      });

      return res;
    } catch (error) {
      console.error(`[阶段性 Prompt] 插入失败:`, error);
      throw error;
    }
  }


  /**
   * 插播消息给LLM进行播放
   * @param {string} roomId - 房间 ID
   * @param {string} userId - 用户 ID
   * @param {string} message - 要插入的内容
   * @param {number} interruptMode - 打断优先级（默认高优先级）
   * @returns {Promise} - API 调用结果
   */
  async insertMessageToAI(roomId, userId, message, interruptMode = 1) {
    if (!this.audioBotEnabled) {
      console.warn('插播消息失败');
      return Promise.reject(new Error('AI Bot 未启用'));
    }
    try {
      const res = await openAPIs.UpdateVoiceChat({
        AppId: APP_ID,
        BusinessId: BUSINESS_ID,
        RoomId: roomId,
        TaskId: userId,
        Command: 'ExternalTextToSpeech',
        Message: message,
        InterruptMode: interruptMode
      });
      return res;
    } catch (error) {
      console.error('插播消息失败:', error);
      throw error;
    }
  }


  /**
   * 完成语音识别（用户停顿超过5秒时自动调用）
   */
  async finishSpeechRecognition(roomId, userId) {
    if (!this.audioBotEnabled) {
      console.warn('[完成语音识别] AI Bot 未启用');
      return Promise.reject(new Error('AI Bot 未启用'));
    }

    try {
      const res = await openAPIs.UpdateVoiceChat({
        AppId: APP_ID,
        BusinessId: BUSINESS_ID,
        RoomId: roomId,
        TaskId: userId,
        Command: 'ExternalTextToLLM',
        Message: '继续, 开始下一个问题，',
        InterruptMode: 3
      })
      return res;
    } catch (error) {
      console.error('[完成语音识别] 调用失败:', error);
      throw error;
    }
  }


  /**
   * 发送文本转语音（TTS）指令
   * @param {string} text - 要播报的文本内容
   * @param {number} interruptMode - 打断优先级（默认高优先级）
   */
  sendTextToSpeech(text, interruptMode = INTERRUPT_PRIORITY.HIGH) {
    if (!this.audioBotEnabled) {
      console.warn('发送失败：AI Bot 未启用');
      return;
    }

    try {
      // 构建 TTS 指令并发送
      this.engine.sendUserBinaryMessage(
        BotName,
        this.stringToTLV(
          JSON.stringify({
            Command: COMMAND.EXTERNAL_TEXT_TO_SPEECH,
            Message: text,
            InterruptMode: interruptMode
          }),
          CommandKey
        )
      );
      console.log(`TTS 指令发送成功：${text}（优先级：${interruptMode}）`);
    } catch (error) {
      console.error('TTS 指令发送失败：', error);
    }
  }
}

export default new RtcClient();